// src/pages/AdCampaignDashboard.tsx

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../contexts/ThemeContext.tsx';
import {
  getAdCampaignMetrics,
  getAdCampaignMediaUrl,
  setAdCampaignInvestmentMonthly,
  getMetaAdsConnection,
  getMetaAdsOAuthUrl,
  getMetaAdsAdAccounts,
  selectMetaAdsAdAccounts,
  disconnectMetaAdsConnection,
  updateAdCampaignAlias,
  type AdCampaignMetrics,
  type AdCampaignIdentity,
  type MetaAdsAdAccount,
  type MetaAdsConnectionStatus,
} from '../services/adCampaignApi.ts';

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { useUserPermissions } from '../hooks/useUserPermissions.ts';

// Importando o componente de filtro mensal
import MetaAdsPixelSettings from '../components/MetaAdsPixelSettings.tsx';
import MonthlyCalendarFilter, { MonthlyFilterType } from '../components/MonthlyCalendarFilter.tsx';

// Icons & Components
import {
  RefreshCw,
  TrendingUp,
  Users,
  ShoppingBag,
  CheckCircle2,
  BarChart2,
  Edit,
  AlertCircle,
  Download,
  Image as ImageIcon,
  Link2,
  ExternalLink,
  Loader2,
  Plug,
  Unplug,
  ChevronRight,
  X,
  Calendar,
  Search,
  Target,
  ArrowUpRight,
  WalletCards,
  Megaphone,
  Pencil,
} from 'lucide-react';

import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar
} from 'recharts';

import ImagePreviewModal from '../components/ImagePreviewModal.tsx';
import InvestmentEditModal from '../components/InvestmentEditModal.tsx';
import AdCampaignNameEditDialog from '../components/AdCampaignNameEditDialog.tsx';
import { API_URL } from '../services/api.ts';
import { DASHBOARD_CHART_COLORS, getDashboardThemeClasses } from '../components/dashboard/dashboardTheme.ts';

interface AdThumbnailProps {
  adId: number | null;
  alt: string;
  isDark: boolean;
  onClick?: () => void;
}

const AdThumbnail: React.FC<AdThumbnailProps> = ({ adId, alt, isDark, onClick }) => {
  const [failed, setFailed] = useState(false);
  const theme = getDashboardThemeClasses(isDark);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={adId === null || !onClick}
      title={adId === null ? 'Criativo não disponível' : 'Ver criativo em tamanho original'}
      className={`group relative h-10 w-10 flex-shrink-0 overflow-hidden rounded-xl border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary disabled:cursor-default ${isDark ? 'border-[#27364D] bg-[#27364D]/45' : 'border-sminy-border bg-slate-100'}`}
    >
      {failed || adId === null ? (
        <div className={`flex h-full w-full items-center justify-center ${theme.mutedText}`}>
          <ImageIcon className="h-4 w-4" />
        </div>
      ) : (
        <img
          src={`${API_URL}/metrics/ad_campaigns/media/${adId}`}
          loading="lazy"
          alt={alt}
          onError={() => setFailed(true)}
          className="h-full w-full object-cover transition-transform group-hover:scale-110"
        />
      )}
    </button>
  );
};

const getAdCampaignDisplayName = (item: AdCampaignMetrics): string => {
  const candidates = [item.displayName, item.metaAdName, item.manualAlias];
  const displayName = candidates.find((candidate) => candidate?.trim())?.trim();
  return displayName || item.sourceId?.trim() || 'Anúncio sem nome';
};

const keepMetricsWithLeads = (data: AdCampaignMetrics[]): AdCampaignMetrics[] => (
  data.filter((item) => Number(item.totalLeads) > 0)
);

const toSafeCsvCell = (value: string | number): string => {
  const withoutControlBreaks = String(value).replace(/[\t\r\n]+/g, ' ');
  const formulaSafeValue = /^[ ]*[=+\-@]/.test(withoutControlBreaks)
    ? `'${withoutControlBreaks}`
    : withoutControlBreaks;
  return `"${formulaSafeValue.replace(/"/g, '""')}"`;
};

interface DashboardKPIs {
  totalLeads: number;
  totalAgendamentos: number;
  totalComparecimentos: number;
  totalVendas: number;
  valorFaturado: number;
  valorPago: number;
  ticketMedio: number;
  investmentTotal: number;
  roasGeral: number;
  cpl: number;    // Custo Por Lead = (investmentTotal / totalLeads)
}

interface ChartData {
  name: string;
  leads: number;
  vendas: number;
  investimento: number;
  roas: number;
}

const AdCampaignDashboard: React.FC = () => {
  const { isDark } = useTheme();
  const theme = getDashboardThemeClasses(isDark);
  const navigate = useNavigate();
  const permissions = useUserPermissions();

  const [metrics, setMetrics] = useState<AdCampaignMetrics[]>([]);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [clinicId, setClinicId] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdateTime, setLastUpdateTime] = useState<string>('');
  const [tableSearch, setTableSearch] = useState<string>('');

  // Tipo de filtro selecionado para o calendário mensal
  const [currentFilter, setCurrentFilter] = useState<MonthlyFilterType>('thisMonth');

  // Dados para o gráfico
  const [chartData, setChartData] = useState<ChartData[]>([]);

  // Estado do modal de imagem
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState<boolean>(false);
  const [selectedAdId, setSelectedAdId] = useState<number | null>(null);

  // Estado do modal de investimento
  const [isInvestmentModalOpen, setIsInvestmentModalOpen] = useState<boolean>(false);
  const [selectedCampaign, setSelectedCampaign] = useState<AdCampaignMetrics | null>(null);

  // Estado do diálogo de nome manual do anúncio
  const [campaignBeingNamed, setCampaignBeingNamed] = useState<AdCampaignMetrics | null>(null);
  const [isCampaignNameSaving, setIsCampaignNameSaving] = useState<boolean>(false);
  const [campaignNameError, setCampaignNameError] = useState<string | null>(null);

  // Estado da integração Meta Ads
  const [metaAdsConnection, setMetaAdsConnection] = useState<MetaAdsConnectionStatus | null>(null);
  const [metaAdsAccounts, setMetaAdsAccounts] = useState<MetaAdsAdAccount[]>([]);
  const [selectedMetaAdAccountIds, setSelectedMetaAdAccountIds] = useState<string[]>([]);
  const [showMetaAccountPicker, setShowMetaAccountPicker] = useState<boolean>(false);
  const [metaAdsLoading, setMetaAdsLoading] = useState<boolean>(false);
  const [metaAdsAccountsLoading, setMetaAdsAccountsLoading] = useState<boolean>(false);
  const [metaAdsSelecting, setMetaAdsSelecting] = useState<boolean>(false);
  const [oauthFeedbackHandled, setOauthFeedbackHandled] = useState<boolean>(false);
  const [isMetaCrmDetailOpen, setIsMetaCrmDetailOpen] = useState(false);
  const [isMetaDrawerOpen, setIsMetaDrawerOpen] = useState<boolean>(false);
  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState<boolean>(false);

  const metaDrawerRef = useRef<HTMLElement>(null);
  const disconnectDialogRef = useRef<HTMLDivElement>(null);
  const [pixelFooterTarget, setPixelFooterTarget] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isMetaDrawerOpen) return;
    const previous = document.activeElement as HTMLElement | null;
    metaDrawerRef.current?.focus();
    return () => { previous?.focus(); };
  }, [isMetaDrawerOpen]);

  useEffect(() => {
    if (!isDisconnectDialogOpen) return;
    const previous = document.activeElement as HTMLElement | null;
    disconnectDialogRef.current?.focus();
    return () => { previous?.focus(); };
  }, [isDisconnectDialogOpen]);

  const handleMetaOverlayKeyDown = (event: React.KeyboardEvent<HTMLElement>, disconnect = false) => {
    if (!disconnect && isDisconnectDialogOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (disconnect) { if (!metaAdsLoading) setIsDisconnectDialogOpen(false); }
      else setIsMetaDrawerOpen(false);
    }
    if (event.key !== 'Tab') return;
    const root = disconnect ? disconnectDialogRef.current : metaDrawerRef.current;
    const elements = Array.from(root?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), select:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    ) || []);
    const first = elements[0];
    const last = elements[elements.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === root)) {
      event.preventDefault(); last?.focus();
    } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === root)) {
      event.preventDefault(); first?.focus();
    }
  };

  // KPIs
  const [kpis, setKpis] = useState<DashboardKPIs>({
    totalLeads: 0,
    totalAgendamentos: 0,
    totalComparecimentos: 0,
    totalVendas: 0,
    valorFaturado: 0,
    valorPago: 0,
    ticketMedio: 0,
    investmentTotal: 0,
    roasGeral: 0,
    cpl: 0,
  });

  const selectedMetaAdsAccounts = metaAdsConnection?.selectedAdAccounts?.length
    ? metaAdsConnection.selectedAdAccounts
    : metaAdsConnection?.selectedAdAccount
      ? [metaAdsConnection.selectedAdAccount]
      : [];
  const isMetaAdsInvestmentAutomated = metaAdsConnection?.status === 'connected' && selectedMetaAdsAccounts.length > 0;

  // ---------------------------
  // 1) Checar permissões e inicializar
  // ---------------------------
  useEffect(() => {
    if (!permissions.hasPermission('prompt.ads')) {
      navigate('/');
      return;
    }
    const storedClinicId = localStorage.getItem('clinic_id');
    if (storedClinicId) {
      setClinicId(Number(storedClinicId));
    }
    handleMonthlyFilterChange('thisMonth');
  }, [navigate, permissions]);

  // ---------------------------
  // 2) Função para definir as datas
  // ---------------------------
  const handleMonthlyFilterChange = (filterType: MonthlyFilterType, customStart?: string, customEnd?: string) => {
    setLoading(true);

    const now = new Date();
    let firstDay: Date;
    let lastDay: Date;

    switch (filterType) {
      case 'thisMonth':
        firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        lastDay = new Date(now);
        break;
      case 'lastMonth':
        firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'last3Months':
        firstDay = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        lastDay = new Date(now);
        break;
      case 'last6Months':
        firstDay = new Date(now.getFullYear(), now.getMonth() - 5, 1);
        lastDay = new Date(now);
        break;
      case 'year':
        firstDay = new Date(now.getFullYear(), 0, 1);
        lastDay = new Date(now);
        break;
      case 'custom':
        if (customStart && customEnd) {
          firstDay = new Date(customStart);
          lastDay = new Date(customEnd);
        } else {
          firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
          lastDay = new Date(now);
        }
        break;
      default:
        firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        lastDay = new Date(now);
    }

    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
    setCurrentFilter(filterType);

    // Buscar dados imediatamente
    const fetchFilteredData = async () => {
      if (!clinicId) {
        setLoading(false);
        return;
      }

      try {
        setError(null);
        const formattedStartDate = firstDay.toISOString().split('T')[0];
        const formattedEndDate = lastDay.toISOString().split('T')[0];

        const data = keepMetricsWithLeads(
          await getAdCampaignMetrics(clinicId, formattedStartDate, formattedEndDate),
        );
        setMetrics(data);
        calculateKPIs(data);
        prepareChartData(data);

        const updateTime = new Date();
        setLastUpdateTime(
          updateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        );
      } catch (err) {
        console.error(err);
        setError('Falha ao obter métricas de anúncios');
      } finally {
        setLoading(false);
      }
    };

    fetchFilteredData();
  };

  // ---------------------------
  // 3) Buscar dados (Refresh manual se precisar)
  // ---------------------------
  const fetchData = async () => {
    if (!clinicId) return;
    try {
      setLoading(true);
      setError(null);

      const data = keepMetricsWithLeads(
        await getAdCampaignMetrics(clinicId, startDate, endDate),
      );
      setMetrics(data);
      calculateKPIs(data);
      prepareChartData(data);

      const now = new Date();
      setLastUpdateTime(
        now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      );
    } catch (err) {
      console.error(err);
      setError('Falha ao obter métricas de anúncios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clinicId > 0) {
      fetchData();
      const hasMetaAdsOAuthFeedback = new URLSearchParams(window.location.search)
        .has('meta_ads_status');
      if (!hasMetaAdsOAuthFeedback) {
        fetchMetaAdsConnection();
      }
    }
  }, [clinicId]);

  useEffect(() => {
    if (clinicId > 0 && !oauthFeedbackHandled) {
      handleMetaAdsOAuthFeedback();
    }
  }, [clinicId, oauthFeedbackHandled]);

  const getMetaAdsErrorMessage = (err: unknown, fallback: string) => {
    const responseError = err as {
      response?: {
        data?: {
          detail?: string | { message?: string };
        };
      };
    };
    const detail = responseError.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail?.message) return detail.message;
    return fallback;
  };

  const fetchMetaAdsConnection = async () => {
    if (!clinicId) return;

    try {
      setMetaAdsLoading(true);
      const connection = await getMetaAdsConnection(clinicId);
      setMetaAdsConnection(connection);
      setSelectedMetaAdAccountIds(
        connection.selectedAdAccounts?.map((account) => account.id)
        || (connection.selectedAdAccount?.id ? [connection.selectedAdAccount.id] : [])
      );
    } catch (err) {
      console.error('Erro ao buscar conexão Meta Ads:', err);
      toast.error(getMetaAdsErrorMessage(err, 'Não foi possível carregar a conexão Meta Ads.'));
    } finally {
      setMetaAdsLoading(false);
    }
  };

  const fetchMetaAdsAccounts = async (openPicker = true) => {
    if (!clinicId) return;

    try {
      setMetaAdsAccountsLoading(true);
      const data = await getMetaAdsAdAccounts(clinicId);
      setMetaAdsAccounts(data.accounts);
      setSelectedMetaAdAccountIds(
        data.selectedAdAccountIds?.length
          ? data.selectedAdAccountIds
          : data.selectedAdAccountId
            ? [data.selectedAdAccountId]
            : data.accounts[0]?.id
              ? [data.accounts[0].id]
              : []
      );
      setShowMetaAccountPicker(openPicker);
    } catch (err) {
      console.error('Erro ao listar contas Meta Ads:', err);
      toast.error(getMetaAdsErrorMessage(err, 'Não foi possível listar as contas Meta Ads.'));
    } finally {
      setMetaAdsAccountsLoading(false);
    }
  };

  const handleConnectMetaAds = async (forceRerequest = false) => {
    if (!clinicId) {
      toast.error('Clínica não identificada.');
      return;
    }

    try {
      setMetaAdsLoading(true);
      const data = await getMetaAdsOAuthUrl(clinicId, forceRerequest);
      window.location.href = data.authorizationUrl;
    } catch (err) {
      console.error('Erro ao gerar URL OAuth Meta Ads:', err);
      setMetaAdsLoading(false);
      toast.error(getMetaAdsErrorMessage(err, 'Não foi possível iniciar a conexão Meta Ads.'));
    }
  };

  const handleSelectMetaAdsAccount = async () => {
    if (selectedMetaAdAccountIds.length === 0) {
      toast.warning('Selecione ao menos uma conta de anúncios.');
      return;
    }

    try {
      setMetaAdsSelecting(true);
      const result = await selectMetaAdsAdAccounts(clinicId, selectedMetaAdAccountIds);
      setMetaAdsConnection(result.connection);
      setShowMetaAccountPicker(false);
      toast.success('Contas Meta Ads selecionadas com sucesso.');
    } catch (err) {
      console.error('Erro ao selecionar conta Meta Ads:', err);
      toast.error(getMetaAdsErrorMessage(err, 'Não foi possível salvar a conta Meta Ads.'));
    } finally {
      setMetaAdsSelecting(false);
    }
  };

  const handleDisconnectMetaAds = async () => {
    try {
      setMetaAdsLoading(true);
      await disconnectMetaAdsConnection(clinicId);
      setMetaAdsConnection({ connected: false, status: 'not_connected', selectedAdAccount: null, selectedAdAccounts: [] });
      setMetaAdsAccounts([]);
      setSelectedMetaAdAccountIds([]);
      setShowMetaAccountPicker(false);
      setIsDisconnectDialogOpen(false);
      setIsMetaDrawerOpen(false);
      toast.success('Conexão Meta Ads removida.');
    } catch (err) {
      console.error('Erro ao desconectar Meta Ads:', err);
      toast.error(getMetaAdsErrorMessage(err, 'Não foi possível remover a conexão Meta Ads.'));
    } finally {
      setMetaAdsLoading(false);
    }
  };

  const handleMetaAdsOAuthFeedback = async () => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('meta_ads_status');
    if (!status) return;

    setOauthFeedbackHandled(true);
    const errorMessage = params.get('meta_ads_error');
    const adAccountCount = params.get('ad_account_count');
    const callbackClinicIdValue = params.get('clinic_id');
    const callbackClinicId = callbackClinicIdValue
      ? Number(callbackClinicIdValue)
      : null;
    const requiresClinicContext = status === 'connected' || status === 'pending_account';

    if (
      requiresClinicContext
      && (
        !Number.isInteger(callbackClinicId)
        || callbackClinicId !== clinicId
      )
    ) {
      toast.error(
        'Não foi possível confirmar que este retorno da Meta pertence à clínica ativa. '
        + 'Volte à clínica que iniciou a conexão e tente novamente.',
      );
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (requiresClinicContext) {
      setIsMetaDrawerOpen(true);
    }
    await fetchMetaAdsConnection();

    switch (status) {
      case 'error':
        toast.error(errorMessage || 'A conexão Meta Ads não foi concluída.');
        break;
      case 'connected':
        toast.success('Meta Ads conectado com conta de anúncios selecionada.');
        await fetchMetaAdsAccounts(true);
        break;
      case 'pending_account': {
        const accountLabel = adAccountCount === '1'
          ? '1 conta disponível'
          : `${adAccountCount || '0'} contas disponíveis`;
        toast.success(`Meta Ads conectado. ${accountLabel} para seleção.`);
        await fetchMetaAdsAccounts(true);
        break;
      }
      default:
        toast.error('A Meta retornou um status de conexão inesperado. Atualize e tente novamente.');
    }

    window.history.replaceState({}, document.title, window.location.pathname);
  };

  // ---------------------------
  // 4) Calcular KPIs
  // ---------------------------
  const calculateKPIs = (data: AdCampaignMetrics[]) => {
    if (!data || data.length === 0) {
      setKpis({
        totalLeads: 0, totalAgendamentos: 0, totalComparecimentos: 0,
        totalVendas: 0, valorFaturado: 0, valorPago: 0, ticketMedio: 0,
        investmentTotal: 0, roasGeral: 0, cpl: 0
      });
      return;
    }
    const totalLeads = data.reduce((sum, item) => sum + item.totalLeads, 0);
    const totalAgendamentos = data.reduce((sum, item) => sum + item.totalAgendamentos, 0);
    const totalComparecimentos = data.reduce((sum, item) => sum + item.totalComparecimentos, 0);
    const totalVendas = data.reduce((sum, item) => sum + item.totalVendas, 0);
    const valorFaturado = data.reduce((sum, item) => sum + item.valorFaturado, 0);
    const valorPago = data.reduce((sum, item) => sum + item.valorPago, 0);
    const investmentTotal = data.reduce((sum, item) => sum + (item.investment || 0), 0);
    const ticketMedio = totalVendas > 0 ? valorFaturado / totalVendas : 0;
    const roasGeral = investmentTotal > 0 ? valorPago / investmentTotal : 0;
    const cpl = totalLeads > 0 ? investmentTotal / totalLeads : 0;

    setKpis({
      totalLeads,
      totalAgendamentos,
      totalComparecimentos,
      totalVendas,
      valorFaturado,
      valorPago,
      ticketMedio,
      investmentTotal,
      roasGeral,
      cpl
    });
  };

  // ---------------------------
  // 5) Preparar dados do gráfico
  // ---------------------------
  const prepareChartData = (data: AdCampaignMetrics[]) => {
    const chartData: ChartData[] = data.map(item => ({
      name: getAdCampaignDisplayName(item),
      leads: item.totalLeads,
      vendas: item.totalVendas,
      investimento: item.investment || 0,
      roas: item.roas || 0,
    }));
    setChartData(chartData);
  };

  // ---------------------------
  // 6) Visualizar imagem
  // ---------------------------
  const handleViewImage = async (adId: number) => {
    try {
      setSelectedAdId(adId);
      setIsImageModalOpen(true);
      setPreviewImageUrl(null);
      setLoading(true);

      const blobUrl = await getAdCampaignMediaUrl(adId);
      setPreviewImageUrl(blobUrl);
    } catch (error) {
      console.error('Erro ao carregar imagem:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
    if (previewImageUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(previewImageUrl);
    }
    setPreviewImageUrl(null);
  };

  // ---------------------------
  // 7) Editar investimento
  // ---------------------------
  const handleUpdateInvestment = (item: AdCampaignMetrics) => {
    if (isMetaAdsInvestmentAutomated) {
      toast.info('Investimento preenchido automaticamente pela Meta Ads.');
      return;
    }

    setSelectedCampaign(item);
    setIsInvestmentModalOpen(true);
  };

  const handleSaveInvestment = async (value: number, referenceMonth: string) => {
    if (!selectedCampaign) return;
    try {
      await setAdCampaignInvestmentMonthly({
        clinic_id: clinicId,
        source_id: selectedCampaign.sourceId,
        investment: value,
        reference_month: `${referenceMonth}-01`,
      });
      setIsInvestmentModalOpen(false);
      toast.success('Investimento mensal atualizado com sucesso!');
      fetchData();
    } catch (error) {
      console.error('Erro ao atualizar investimento mensal:', error);
      toast.error('Não foi possível atualizar o investimento.');
    }
  };

  const handleOpenCampaignNameDialog = (item: AdCampaignMetrics) => {
    // Compatibilidade segura: respostas antigas não enviam canEditName e não
    // devem liberar uma escrita que o servidor ainda não autorizou.
    if (item.canEditName !== true) return;
    setCampaignNameError(null);
    setCampaignBeingNamed(item);
  };

  const handleCloseCampaignNameDialog = () => {
    if (isCampaignNameSaving) return;
    setCampaignBeingNamed(null);
    setCampaignNameError(null);
  };

  const handleSaveCampaignName = async (alias: string | null) => {
    if (!campaignBeingNamed || campaignBeingNamed.canEditName !== true) return;

    try {
      setIsCampaignNameSaving(true);
      setCampaignNameError(null);
      const identity: AdCampaignIdentity = await updateAdCampaignAlias(
        clinicId,
        campaignBeingNamed.sourceId,
        alias,
      );
      const nextMetrics = metrics.map((item) => (
        item.sourceId === identity.sourceId
          ? { ...item, ...identity }
          : item
      ));

      setMetrics(nextMetrics);
      prepareChartData(nextMetrics);
      setCampaignBeingNamed(null);
      toast.success(alias ? 'Nome do anúncio atualizado.' : 'Nome personalizado removido.');
    } catch (err) {
      setCampaignNameError(
        getMetaAdsErrorMessage(err, 'Não foi possível salvar o nome do anúncio.'),
      );
    } finally {
      setIsCampaignNameSaving(false);
    }
  };

  // ---------------------------
  // 8) Format date BR
  // ---------------------------
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  // ---------------------------
  // 9) Cor do ROAS
  // ---------------------------
  const getRoasColorClass = (roas: number) => {
    if (roas >= 4) return isDark ? 'bg-[#38C982]/15 text-[#72E3A8]' : 'bg-emerald-50 text-emerald-700';
    if (roas >= 2) return isDark ? 'bg-amber-500/15 text-amber-300' : 'bg-amber-50 text-amber-700';
    return isDark ? 'bg-rose-500/15 text-rose-300' : 'bg-rose-50 text-rose-700';
  };

  const getMetaAdsStatusLabel = (status?: string) => {
    switch (status) {
      case 'connected':
        return 'Conectado';
      case 'pending_account':
        return 'Selecionar conta';
      case 'error':
        return 'Erro';
      case 'disconnected':
      case 'not_connected':
      default:
        return 'Não conectado';
    }
  };

  const getMetaAdsStatusClass = (status?: string) => {
    switch (status) {
      case 'connected':
        return isDark ? 'bg-[#38C982]/15 text-[#72E3A8]' : 'bg-emerald-50 text-emerald-700';
      case 'pending_account':
        return isDark ? 'bg-amber-500/15 text-amber-300' : 'bg-amber-50 text-amber-700';
      case 'error':
        return isDark ? 'bg-rose-500/15 text-rose-300' : 'bg-rose-50 text-rose-700';
      default:
        return isDark ? 'bg-[#27364D]/45 text-[#A6B2C3]' : 'bg-slate-100 text-sminy-text-secondary';
    }
  };

  const formatMetaAccountStatus = (status?: number) => {
    if (status === 1) return 'Ativa';
    if (status === 2) return 'Desativada';
    if (status === 3) return 'Não liquidada';
    if (status === 7) return 'Pendente';
    if (status === 9) return 'Em revisão';
    if (status === 100) return 'Fechada';
    return status ? `Status ${status}` : 'Status indisponível';
  };

  const toggleMetaAdAccountSelection = (accountId: string) => {
    setSelectedMetaAdAccountIds((currentIds) => (
      currentIds.includes(accountId)
        ? currentIds.filter((id) => id !== accountId)
        : [...currentIds, accountId]
    ));
  };

  const renderMetaAdsBanner = () => {
    const status = metaAdsConnection?.status || 'not_connected';
    const selectedAccounts = selectedMetaAdsAccounts;
    const selectedAccount = selectedAccounts[0];
    const hasConnection = status !== 'not_connected' && status !== 'disconnected';
    const accountLabel = selectedAccounts.length > 1
      ? `${selectedAccounts.length} contas ativas`
      : selectedAccount?.name || selectedAccount?.id;

    return (
      <>
        <section className={`rounded-2xl border p-4 ${theme.surface}`}>
          <div className="flex flex-wrap items-center gap-3">
            <div className="rounded-xl p-2 bg-sminy-primary/10 text-sminy-primary flex-shrink-0">
              <Link2 className="h-5 w-5" />
            </div>

            <div className="flex flex-1 min-w-0 flex-wrap items-center gap-2">
              <span className={`text-sm font-semibold ${theme.primaryText}`}>Meta Ads</span>
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${getMetaAdsStatusClass(status)}`}>
                {getMetaAdsStatusLabel(status)}
              </span>
              {hasConnection && accountLabel ? (
                  <span className={`truncate text-sm ${theme.secondaryText}`}>
                  · {accountLabel}
                </span>
              ) : hasConnection ? (
                  <span className={`truncate text-sm ${theme.secondaryText}`}>
                  · Selecione a conta de anúncios
                </span>
              ) : (
                  <span className={`truncate text-sm ${theme.secondaryText}`}>
                  · Conecte sua conta para automatizar o investimento
                </span>
              )}
            </div>

            {hasConnection ? (
              <button
                type="button"
                onClick={() => setIsMetaDrawerOpen(true)}
                className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-xl border px-3 py-2 text-sm transition-colors ${theme.control}`}
              >
                Gerenciar
                <ChevronRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => handleConnectMetaAds(false)}
                disabled={metaAdsLoading}
                className="inline-flex items-center gap-1.5 rounded-xl bg-sminy-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-sminy-primary/90 transition-colors disabled:cursor-not-allowed disabled:opacity-60 flex-shrink-0"
              >
                {metaAdsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                Conectar
              </button>
            )}
          </div>

          {metaAdsConnection?.lastError && !isMetaDrawerOpen && (
            <div className={`mt-3 rounded-xl border p-3 text-xs ${isDark ? 'border-rose-500/30 bg-rose-500/10 text-rose-300' : 'border-rose-200 bg-rose-50 text-rose-700'}`}>
              {metaAdsConnection.lastError}
            </div>
          )}
        </section>

        {renderMetaAdsDrawer()}
        {renderDisconnectDialog()}
      </>
    );
  };

  const renderMetaAdsDrawer = () => {
    if (!isMetaDrawerOpen) return null;

    const status = metaAdsConnection?.status || 'not_connected';
    const selectedAccounts = selectedMetaAdsAccounts;
    const hasConnection = status !== 'not_connected' && status !== 'disconnected';

    return createPortal(
      <>
        <div
          className="fixed inset-0 z-[9996] bg-black/40 backdrop-blur-sm"
          onClick={() => setIsMetaDrawerOpen(false)}
        />
        <aside
          ref={metaDrawerRef}
          role="dialog"
          aria-modal={!isDisconnectDialogOpen && !isMetaCrmDetailOpen}
          aria-hidden={isDisconnectDialogOpen || isMetaCrmDetailOpen || undefined}
          aria-labelledby="meta-ads-drawer-title"
          tabIndex={-1}
          onKeyDown={event => handleMetaOverlayKeyDown(event)}
          className={`fixed right-0 top-0 z-[9997] flex h-full w-full max-w-[480px] flex-col shadow-2xl ${theme.surface}`}
        >
          {/* Drawer Header */}
          <div className={`flex items-center justify-between border-b px-5 py-4 ${theme.border}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="rounded-xl p-2 bg-sminy-primary/10 text-sminy-primary flex-shrink-0">
                <Link2 className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h2 id="meta-ads-drawer-title" className={`text-base font-semibold ${theme.primaryText}`}>Integração Meta Ads</h2>
                <span className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${getMetaAdsStatusClass(status)}`}>
                  {getMetaAdsStatusLabel(status)}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsMetaDrawerOpen(false)}
              className={`flex-shrink-0 rounded-xl p-2 transition-colors ${isDark ? 'text-[#A6B2C3] hover:bg-[#27364D]/45' : 'text-sminy-muted hover:bg-slate-100'}`}
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Drawer Body */}
          <div className="min-h-0 flex-1 overflow-y-auto p-5 space-y-4">
            {/* Status info */}
            <div className={`rounded-xl border p-4 ${isDark ? 'border-[#27364D] bg-[#071225]/45' : 'border-sminy-border bg-slate-50'}`}>
              <p className={`text-sm ${theme.secondaryText}`}>
                {hasConnection
                  ? selectedAccounts.length > 0
                    ? 'Investimento sincronizado automaticamente.'
                    : 'Login concluído. Selecione a conta de anúncios abaixo.'
                  : 'Conecte a conta de anúncios para automatizar o investimento.'}
              </p>
              {metaAdsConnection?.metaUserName && (
                <p className={`mt-2 text-xs ${theme.mutedText}`}>
                  Usuário Meta: {metaAdsConnection.metaUserName}
                </p>
              )}
            </div>

            {/* Active accounts */}
            {selectedAccounts.length > 0 && (
              <div>
                <span className={`mb-2 block text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  Contas ativas
                </span>
                <div className="grid gap-2">
                  {selectedAccounts.map((account) => (
                    <div key={account.id} className={`rounded-xl border p-3 ${isDark ? 'border-[#27364D] bg-[#0F1D31]' : 'border-sminy-border bg-white'}`}>
                      <p className={`truncate text-sm font-medium ${theme.primaryText}`}>{account.name || account.id}</p>
                      <p className={`mt-1 text-xs ${theme.mutedText}`}>{account.id} · {account.currency || '-'}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Last error */}
            {metaAdsConnection?.lastError && (
              <div className={`rounded-xl border p-3 text-sm ${isDark ? 'border-rose-500/30 bg-rose-500/10 text-rose-300' : 'border-rose-200 bg-rose-50 text-rose-700'}`}>
                {metaAdsConnection.lastError}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={fetchMetaAdsConnection}
                disabled={metaAdsLoading}
                className={`inline-flex items-center rounded-xl border px-3 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${theme.control}`}
              >
                {metaAdsLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Atualizar
              </button>

              <button
                type="button"
                onClick={() => handleConnectMetaAds(hasConnection)}
                disabled={metaAdsLoading}
                className="inline-flex items-center rounded-xl bg-sminy-primary px-3 py-2 text-sm font-medium text-white hover:bg-sminy-primary/90 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
              >
                {metaAdsLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ExternalLink className="mr-2 h-4 w-4" />}
                {hasConnection ? 'Reconectar' : 'Conectar'}
              </button>

              {hasConnection && (
                <>
                  <button
                    type="button"
                    onClick={() => fetchMetaAdsAccounts(true)}
                    disabled={metaAdsAccountsLoading}
                    className={`inline-flex items-center rounded-xl border px-3 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${theme.control}`}
                  >
                    {metaAdsAccountsLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plug className="mr-2 h-4 w-4" />}
                    Contas
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsDisconnectDialogOpen(true)}
                    disabled={metaAdsLoading}
                    className={`inline-flex items-center rounded-xl border px-3 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${isDark
                      ? 'border-rose-500/40 text-rose-300 hover:bg-rose-500/10'
                      : 'border-rose-200 text-rose-700 hover:bg-rose-50'
                      }`}
                  >
                    <Unplug className="mr-2 h-4 w-4" />
                    Remover
                  </button>
                </>
              )}
            </div>

            {/* Account picker */}
            {showMetaAccountPicker && (
              <div className={`rounded-xl border p-4 ${isDark ? 'border-[#27364D] bg-[#071225]/45' : 'border-sminy-border bg-slate-50'}`}>
                <span className={`mb-2 block text-sm font-medium ${theme.secondaryText}`}>
                  Contas de anúncios
                </span>
                <div className="grid gap-2">
                  {metaAdsAccounts.length === 0 && (
                    <div className={`rounded-xl border p-3 text-sm ${isDark ? 'border-[#27364D] text-[#A6B2C3]' : 'border-sminy-border text-sminy-muted'}`}>
                      Nenhuma conta encontrada
                    </div>
                  )}
                  {metaAdsAccounts.map((account) => {
                    const isSelected = selectedMetaAdAccountIds.includes(account.id);
                    return (
                      <label
                        key={account.id}
                        className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 text-sm transition-colors ${isSelected
                          ? 'border-sminy-primary bg-sminy-primary/10'
                          : isDark
                            ? 'border-[#27364D] bg-[#0F1D31] hover:bg-[#27364D]/35'
                            : 'border-sminy-border bg-white hover:bg-slate-50'
                          }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleMetaAdAccountSelection(account.id)}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-sminy-primary focus:ring-sminy-primary"
                        />
                        <span className="min-w-0 flex-1">
                          <span className={`block truncate font-medium ${theme.primaryText}`}>
                            {account.name || account.id}
                          </span>
                          <span className={`text-xs ${theme.mutedText}`}>
                            {account.id} · {account.currency || 'sem moeda'} · {formatMetaAccountStatus(account.account_status)}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            {hasConnection && clinicId > 0 && !metaAdsLoading && !metaAdsSelecting && (
              <MetaAdsPixelSettings
                key={JSON.stringify(metaAdsConnection)}
                clinicId={clinicId}
                accounts={selectedAccounts}
                isDark={isDark}
                footerTarget={pixelFooterTarget}
                onDetailOpenChange={setIsMetaCrmDetailOpen}
              />
            )}
          </div>

          {hasConnection && (
            <div ref={setPixelFooterTarget} className={`shrink-0 border-t px-5 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] ${theme.border}`} />
          )}
          {/* Account selection retains its own explicit action. */}
          {showMetaAccountPicker && (
            <div className={`shrink-0 border-t px-5 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] ${theme.border}`}>
              <button
                type="button"
                onClick={handleSelectMetaAdsAccount}
                disabled={selectedMetaAdAccountIds.length === 0 || metaAdsSelecting}
                className="inline-flex w-full items-center justify-center rounded-xl bg-sminy-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-sminy-primary/90 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
              >
                {metaAdsSelecting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                Salvar {selectedMetaAdAccountIds.length || ''} conta(s)
              </button>
            </div>
          )}
        </aside>
      </>,
      document.body
    );
  };

  const renderDisconnectDialog = () => {
    if (!isDisconnectDialogOpen) return null;

    return createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => !metaAdsLoading && setIsDisconnectDialogOpen(false)}
        />
        <div ref={disconnectDialogRef} role="dialog" aria-modal="true" aria-labelledby="meta-ads-disconnect-title" tabIndex={-1}
          onKeyDown={event => handleMetaOverlayKeyDown(event, true)}
          className={`relative z-[10000] w-full max-w-md rounded-2xl shadow-2xl ${theme.surface}`}>
          <div className={`border-b px-6 py-4 ${theme.border}`}>
            <h3 id="meta-ads-disconnect-title" className={`text-lg font-semibold ${theme.primaryText}`}>
              Remover conexão Meta Ads
            </h3>
          </div>
          <div className="px-6 py-4">
            <p className={`text-sm ${theme.secondaryText}`}>
              Tem certeza que deseja remover a conexão Meta Ads desta clínica? O investimento deixará de ser sincronizado automaticamente.
            </p>
          </div>
          <div className={`flex justify-end gap-3 border-t px-6 py-4 ${theme.border}`}>
            <button
              type="button"
              onClick={() => setIsDisconnectDialogOpen(false)}
              disabled={metaAdsLoading}
              className={`rounded-xl border px-4 py-2 text-sm transition-colors disabled:opacity-60 ${theme.control}`}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleDisconnectMetaAds}
              disabled={metaAdsLoading}
              className="inline-flex items-center rounded-xl bg-rose-500 px-4 py-2 text-sm font-medium text-white hover:bg-rose-600 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
            >
              {metaAdsLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Unplug className="mr-2 h-4 w-4" />}
              Remover
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  // ---------------------------
  // 10) Renderizar gráfico
  // ---------------------------
  const renderChart = () => {
    if (chartData.length === 0) {
      return (
        <div className="flex items-center justify-center h-64">
          <p className={isDark ? 'text-gray-500' : 'text-gray-500'}>Nenhum dado disponível para o gráfico</p>
        </div>
      );
    }

    const gridColor = isDark ? '#27364D' : '#E4E8EF';
    const textColor = isDark ? '#7E8CA0' : '#667085';

    return (
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={chartData} margin={{ top: 16, right: 16, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="4 4" stroke={gridColor} vertical={false} />
          <XAxis
            dataKey="name"
            stroke={textColor}
            fontSize={12}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value: string) => value.length > 18 ? `${value.slice(0, 18)}…` : value}
          />
          <YAxis stroke={textColor} fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
          <Tooltip
            cursor={{ fill: isDark ? 'rgba(var(--sminy-primary-rgb),0.06)' : 'rgba(var(--sminy-primary-rgb),0.08)' }}
            contentStyle={{
              backgroundColor: isDark ? '#0F1D31' : '#ffffff',
              border: `1px solid ${isDark ? '#27364D' : '#E4E8EF'}`,
              borderRadius: '12px',
              boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15)',
              fontSize: '13px',
              padding: '8px 12px'
            }}
            labelStyle={{ color: isDark ? '#F8FAFC' : '#121B29', fontWeight: 600, marginBottom: '4px' }}
            itemStyle={{ color: isDark ? '#A6B2C3' : '#344054' }}
          />
          <Legend
            iconType="circle"
            wrapperStyle={{ paddingBottom: '12px', fontSize: '13px', color: textColor }}
            verticalAlign="top"
            align="right"
          />
          <Bar dataKey="leads" name="Leads" fill={DASHBOARD_CHART_COLORS.leads} radius={[6, 6, 0, 0]} maxBarSize={40} />
          <Bar dataKey="vendas" name="Vendas" fill={DASHBOARD_CHART_COLORS.sales} radius={[6, 6, 0, 0]} maxBarSize={40} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  // ---------------------------
  // 11) Exportar CSV
  // ---------------------------
  const exportToCSV = () => {
    if (metrics.length === 0) return;
    const headers = 'NomeAnuncio,IDAnuncio,CampanhaMeta,Leads,Agendamentos,Comparecimentos,Vendas,ValorFaturado,TicketMedio,Investment,CPL,ROAS\n';
    const rows = metrics.map(item => {
      const cpl = item.totalLeads > 0 ? (item.investment || 0) / item.totalLeads : 0;
      return [
        toSafeCsvCell(getAdCampaignDisplayName(item)),
        toSafeCsvCell(item.sourceId),
        toSafeCsvCell(item.metaCampaignName || ''),
        item.totalLeads,
        item.totalAgendamentos,
        item.totalComparecimentos,
        item.totalVendas,
        item.valorFaturado,
        item.ticketMedio,
        item.investment,
        cpl.toFixed(2),
        item.roas,
      ].join(',');
    }).join('\n');

    const csvContent = `data:text/csv;charset=utf-8,${headers}${rows}`;
    const encodedUri = encodeURI(csvContent);

    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `metricas_${formatDate(startDate)}_${formatDate(endDate)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatCurrency = (value: number) => `R$ ${value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

  const formatPercentage = (value: number, total: number) => {
    const percentage = total > 0 ? (value / total) * 100 : 0;
    return `${percentage.toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })}%`;
  };

  const filteredMetrics = metrics.filter((item) => {
    const query = tableSearch.trim().toLowerCase();
    if (!query) return true;
    return [
      getAdCampaignDisplayName(item),
      item.sourceId,
      item.metaCampaignName,
      item.metaCampaignId,
    ].some((value) => value?.toLowerCase().includes(query));
  });

  const selectedPreviewCampaign = selectedAdId === null
    ? null
    : metrics.find((item) => item.adId === selectedAdId) || null;

  const insightMetrics = [
    { label: 'Leads', value: kpis.totalLeads.toLocaleString('pt-BR') },
    {
      label: 'Agendamentos',
      value: `${kpis.totalAgendamentos.toLocaleString('pt-BR')} (${formatPercentage(kpis.totalAgendamentos, kpis.totalLeads)})`,
    },
    {
      label: 'Comparecimentos',
      value: `${kpis.totalComparecimentos.toLocaleString('pt-BR')} (${formatPercentage(kpis.totalComparecimentos, kpis.totalAgendamentos)})`,
    },
    {
      label: 'Vendas',
      value: `${kpis.totalVendas.toLocaleString('pt-BR')} (${formatPercentage(kpis.totalVendas, kpis.totalComparecimentos)})`,
    },
    { label: 'Ticket médio', value: formatCurrency(kpis.ticketMedio) },
    { label: 'Valor faturado', value: formatCurrency(kpis.valorFaturado) },
    { label: 'Investido', value: formatCurrency(kpis.investmentTotal) },
  ];

  const kpiCards = [
    {
      label: 'Investimento',
      value: formatCurrency(kpis.investmentTotal),
      helper: isMetaAdsInvestmentAutomated ? 'Sincronizado pela Meta' : 'No período',
      Icon: WalletCards,
      iconClass: isDark ? 'bg-sminy-primary/15 text-[#66A3FF]' : 'bg-sminy-blue-light text-sminy-primary',
      badge: isMetaAdsInvestmentAutomated ? 'Auto' : undefined,
    },
    {
      label: 'Leads',
      value: kpis.totalLeads.toLocaleString('pt-BR'),
      helper: 'Conversões captadas',
      Icon: Users,
      iconClass: isDark ? 'bg-[#9B7BFF]/15 text-[#B8A5FF]' : 'bg-violet-50 text-violet-600',
    },
    {
      label: 'CPL',
      value: formatCurrency(kpis.cpl),
      helper: 'Custo por lead',
      Icon: Target,
      iconClass: isDark ? 'bg-[#22C7D6]/15 text-[#67DBE5]' : 'bg-cyan-50 text-cyan-600',
    },
    {
      label: 'Vendas',
      value: kpis.totalVendas.toLocaleString('pt-BR'),
      helper: 'Conversões em venda',
      Icon: ShoppingBag,
      iconClass: isDark ? 'bg-[#38C982]/15 text-[#72E3A8]' : 'bg-emerald-50 text-emerald-600',
    },
    {
      label: 'ROAS',
      value: `${kpis.roasGeral.toFixed(2)}x`,
      helper: 'Retorno sobre investimento',
      Icon: TrendingUp,
      iconClass: isDark ? 'bg-purple-500/15 text-purple-300' : 'bg-purple-50 text-purple-600',
    },
  ];


  return (
    <div data-testid="meta-ads-dashboard" data-theme={isDark ? 'dark' : 'light'} className={`min-h-screen w-full transition-colors ${theme.canvas}`}>
      <header className={`sticky top-0 z-20 border-b px-4 py-4 backdrop-blur ${theme.border} ${isDark ? 'bg-[#071225]/95' : 'bg-[#F7F9FC]/95'}`}>
        <div className="mx-auto flex max-w-[1440px] flex-col gap-4 sm:px-2 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <p className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${theme.accentText}`}>Relatórios / Meta Ads</p>
            <h1 className={`mt-1 text-2xl font-semibold tracking-tight ${theme.primaryText}`}>Meta Ads Insights</h1>
            <div className={`mt-1 flex flex-wrap items-center gap-2 text-xs ${theme.mutedText}`}>
              <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
              {startDate && endDate ? (
                <span>{formatDate(startDate)} → {formatDate(endDate)}</span>
              ) : (
                <span>Desempenho das campanhas no período</span>
              )}
              {!loading && metrics.length > 0 && (
                <>
                  <span aria-hidden>·</span>
                  <span>{metrics.length} {metrics.length === 1 ? 'anúncio' : 'anúncios'}</span>
                </>
              )}
              {lastUpdateTime && <span className="hidden sm:inline">· Atualizado às {lastUpdateTime}</span>}
            </div>
          </div>

          <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center lg:w-auto">
            <button
              type="button"
              onClick={fetchData}
              disabled={loading || !clinicId}
              className={`inline-flex h-10 items-center justify-center gap-2 rounded-xl border px-3 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${theme.control}`}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
              Atualizar
            </button>
            <MonthlyCalendarFilter
              onFilterSelect={handleMonthlyFilterChange}
              currentFilter={currentFilter}
              startDate={startDate}
              endDate={endDate}
              isLoading={loading}
              className="w-full sm:w-64"
            />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1440px] space-y-5 px-4 py-6 sm:px-6 lg:px-8">
        {error && (
          <div role="alert" className={`flex items-center gap-3 rounded-2xl border p-4 ${isDark ? 'border-rose-500/30 bg-rose-500/10 text-rose-300' : 'border-rose-200 bg-rose-50 text-rose-700'}`}>
            <AlertCircle className="h-5 w-5 shrink-0" aria-hidden="true" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {clinicId > 0 && renderMetaAdsBanner()}

        {loading && !error && (
          <div className={`flex items-center justify-center gap-3 rounded-2xl border p-10 ${theme.surface}`}>
            <RefreshCw className={`h-6 w-6 animate-spin ${theme.accentText}`} aria-hidden="true" />
            <p className={`text-sm ${theme.secondaryText}`}>Carregando métricas...</p>
          </div>
        )}

        {!loading && !error && metrics.length === 0 && (
          <div className={`rounded-2xl border p-10 text-center ${theme.surface}`}>
            <span className={`mx-auto mb-4 grid h-12 w-12 place-items-center rounded-2xl ${theme.accentSurface}`}>
              <Megaphone className="h-6 w-6" aria-hidden="true" />
            </span>
            <h2 className={`text-lg font-semibold ${theme.primaryText}`}>Nenhuma campanha encontrada</h2>
            <p className={`mt-1 text-sm ${theme.mutedText}`}>Tente ajustar os filtros de data para carregar outros anúncios.</p>
          </div>
        )}

        {!loading && !error && metrics.length > 0 && (
          <>
            <section aria-label="Principais indicadores" className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
              {kpiCards.map(({ label, value, helper, Icon, iconClass, badge }) => (
                <article key={label} className={`rounded-2xl border p-4 ${theme.surface}`}>
                  <div className="flex items-start justify-between gap-3">
                    <span className={`grid h-10 w-10 place-items-center rounded-xl ${iconClass}`}>
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    {badge ? (
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium ${theme.accentSurface}`}>
                        <Link2 className="h-3 w-3" aria-hidden="true" />
                        {badge}
                      </span>
                    ) : (
                      <ArrowUpRight className={`h-4 w-4 ${theme.mutedText}`} aria-hidden="true" />
                    )}
                  </div>
                  <p className={`mt-4 text-sm ${theme.secondaryText}`}>{label}</p>
                  <p className={`mt-1 truncate text-2xl font-semibold tracking-tight ${theme.primaryText}`} title={value}>{value}</p>
                  <p className={`mt-2 text-xs ${theme.mutedText}`}>{helper}</p>
                </article>
              ))}
            </section>

            <section className="grid gap-5 lg:grid-cols-[minmax(0,1.65fr)_minmax(280px,0.75fr)]">
              <article aria-label="Desempenho das campanhas" className={`overflow-hidden rounded-2xl border ${theme.surface}`}>
                <header className={`flex items-start justify-between gap-3 border-b px-5 py-4 ${theme.border}`}>
                  <div>
                    <h2 className={`text-base font-semibold tracking-tight ${theme.primaryText}`}>Desempenho das campanhas</h2>
                    <p className={`mt-1 text-xs leading-5 ${theme.mutedText}`}>Comparativo de leads e vendas por anúncio no período.</p>
                  </div>
                  <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${theme.accentSurface}`}>
                    <BarChart2 className="h-4 w-4" aria-hidden="true" />
                  </span>
                </header>
                <div className="p-4 sm:p-5">{renderChart()}</div>
              </article>

              <article aria-label="Insights de performance" className={`rounded-2xl border p-5 ${theme.surface}`}>
                <header className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className={`text-base font-semibold tracking-tight ${theme.primaryText}`}>Insights</h2>
                    <p className={`mt-1 text-xs leading-5 ${theme.mutedText}`}>Leitura rápida do período selecionado.</p>
                  </div>
                  <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${theme.accentSurface}`}>
                    <Target className="h-4 w-4" aria-hidden="true" />
                  </span>
                </header>

                <dl data-testid="insights-metrics" className={`mt-5 divide-y ${theme.divide}`}>
                  {insightMetrics.map(({ label, value }) => (
                    <div key={label} className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                      <dt className={`text-sm ${theme.secondaryText}`}>{label}</dt>
                      <dd className={`text-right text-sm font-semibold ${theme.primaryText}`}>{value}</dd>
                    </div>
                  ))}
                </dl>
              </article>
            </section>

            <section aria-label="Métricas por anúncio" className={`overflow-hidden rounded-2xl border ${theme.surface}`}>
              <header className={`flex flex-col gap-4 border-b px-4 py-5 sm:px-5 lg:flex-row lg:items-center lg:justify-between ${theme.border}`}>
                <div>
                  <h2 className={`text-base font-semibold tracking-tight ${theme.primaryText}`}>Métricas por anúncio</h2>
                  <p className={`mt-1 text-xs leading-5 ${theme.mutedText}`}>Cada linha representa um anúncio individual no período.</p>
                </div>
                <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto">
                  <label className="relative min-w-0 flex-1 sm:w-64">
                    <Search className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${theme.mutedText}`} aria-hidden="true" />
                    <input
                      type="search"
                      value={tableSearch}
                      onChange={(event) => setTableSearch(event.target.value)}
                      placeholder="Buscar nome ou ID do anúncio"
                      aria-label="Buscar anúncios"
                      className={`h-10 w-full rounded-xl border py-2 pl-9 pr-3 text-sm outline-none transition-colors ${isDark ? 'placeholder:text-[#7E8CA0]' : 'placeholder:text-slate-400'} focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/15 ${theme.input}`}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={exportToCSV}
                    className={`inline-flex h-10 items-center justify-center gap-2 rounded-xl border px-3 text-sm font-medium transition-colors ${theme.control}`}
                  >
                    <Download className="h-4 w-4" aria-hidden="true" />
                    Exportar CSV
                  </button>
                </div>
              </header>

              <div className="max-h-[70vh] overflow-auto">
                <table className={`min-w-full divide-y ${theme.divide}`}>
                  <thead className={`sticky top-0 z-10 ${isDark ? 'bg-[#071225]' : 'bg-slate-50'}`}>
                    <tr>
                      {['Anúncio', 'Leads', 'CPL', 'Agend.', 'Comparec.', 'Vendas', 'Faturado', 'Ticket Médio', 'Invest.', 'ROAS', 'Ações'].map((header) => (
                        <th key={header} className={`whitespace-nowrap px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] ${theme.mutedText}`}>
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${theme.divide}`}>
                    {filteredMetrics.length === 0 ? (
                      <tr>
                        <td colSpan={11} className={`px-5 py-10 text-center text-sm ${theme.mutedText}`}>Nenhum anúncio corresponde à busca.</td>
                      </tr>
                    ) : filteredMetrics.map((item, idx) => {
                      const cpl = item.totalLeads > 0 ? (item.investment || 0) / item.totalLeads : 0;
                      const displayName = getAdCampaignDisplayName(item);
                      const mediaAdId = item.adId;
                      return (
                        <tr
                          key={mediaAdId === null ? item.sourceId : mediaAdId}
                          className={`transition-colors ${idx % 2 === 0 ? (isDark ? 'bg-[#0F1D31]' : 'bg-white') : (isDark ? 'bg-[#071225]/25' : 'bg-slate-50/70')} ${isDark ? 'hover:bg-[#27364D]/35' : 'hover:bg-slate-50'}`}
                        >
                          <td className="whitespace-nowrap px-5 py-4">
                            <div className="flex items-center gap-3">
                              <AdThumbnail
                                adId={mediaAdId}
                                alt={displayName}
                                isDark={isDark}
                                onClick={mediaAdId !== null ? () => handleViewImage(mediaAdId) : undefined}
                              />
                              <div className="group/name min-w-0">
                                <div className="flex min-w-0 items-center gap-1.5">
                                  <div
                                    className={`max-w-[220px] truncate text-sm font-medium ${theme.primaryText}`}
                                    title={displayName}
                                  >
                                    {displayName}
                                  </div>
                                  {item.nameOrigin === 'meta' && (
                                    <span
                                      title="Nome sincronizado pela Meta Ads"
                                      aria-label="Nome sincronizado pela Meta Ads"
                                      className={`inline-flex h-6 shrink-0 items-center gap-1 rounded-lg px-1.5 text-[10px] font-medium ${theme.accentSurface}`}
                                    >
                                      <Link2 className="h-3 w-3" aria-hidden="true" />
                                      Meta
                                    </span>
                                  )}
                                  {item.canEditName === true && (
                                    <button
                                      type="button"
                                      onClick={() => handleOpenCampaignNameDialog(item)}
                                      aria-label={`Editar nome do anúncio ${displayName}`}
                                      aria-haspopup="dialog"
                                      title="Editar nome do anúncio"
                                      className={`grid h-10 w-10 shrink-0 place-items-center rounded-lg opacity-100 transition-colors focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary md:h-7 md:w-7 md:opacity-0 md:group-hover/name:opacity-100 md:group-focus-within/name:opacity-100 ${isDark ? 'text-[#A6B2C3] hover:bg-[#27364D]/45 hover:text-[#F8FAFC]' : 'text-sminy-muted hover:bg-slate-100 hover:text-sminy-primary'}`}
                                    >
                                      <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                                    </button>
                                  )}
                                </div>
                                <div className={`mt-1 max-w-[280px] truncate text-xs ${theme.mutedText}`} title={`ID do anúncio: ${item.sourceId}`}>
                                  ID do anúncio: <span className="font-mono">{item.sourceId}</span>
                                </div>
                                {item.metaCampaignName && (
                                  <div className={`mt-0.5 max-w-[280px] truncate text-[11px] ${theme.mutedText}`} title={`Campanha: ${item.metaCampaignName}`}>
                                    Campanha: {item.metaCampaignName}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className={`whitespace-nowrap px-5 py-4 text-sm ${theme.secondaryText}`}>{item.totalLeads.toLocaleString('pt-BR')}</td>
                          <td className={`whitespace-nowrap px-5 py-4 text-sm ${theme.secondaryText}`}>{formatCurrency(cpl)}</td>
                          <td className={`whitespace-nowrap px-5 py-4 text-sm ${theme.secondaryText}`}>
                            {item.totalAgendamentos.toLocaleString('pt-BR')}<br />
                            <span className={`text-xs ${theme.mutedText}`}>{item.totalLeads > 0 ? `${((item.totalAgendamentos / item.totalLeads) * 100).toFixed(1)}%` : '0%'} dos leads</span>
                          </td>
                          <td className={`whitespace-nowrap px-5 py-4 text-sm ${theme.secondaryText}`}>
                            {item.totalComparecimentos.toLocaleString('pt-BR')}<br />
                            <span className={`text-xs ${theme.mutedText}`}>{item.totalAgendamentos > 0 ? `${((item.totalComparecimentos / item.totalAgendamentos) * 100).toFixed(1)}%` : '0%'} dos agend.</span>
                          </td>
                          <td className={`whitespace-nowrap px-5 py-4 text-sm ${theme.secondaryText}`}>
                            {item.totalVendas.toLocaleString('pt-BR')}<br />
                            <span className={`text-xs ${theme.mutedText}`}>{item.totalLeads > 0 ? `${((item.totalVendas / item.totalLeads) * 100).toFixed(1)}%` : '0%'} dos leads</span>
                          </td>
                          <td className={`whitespace-nowrap px-5 py-4 text-sm ${theme.secondaryText}`}>{formatCurrency(item.valorFaturado)}</td>
                          <td className={`whitespace-nowrap px-5 py-4 text-sm ${theme.secondaryText}`}>{formatCurrency(item.ticketMedio)}</td>
                          <td className={`whitespace-nowrap px-5 py-4 text-sm ${theme.secondaryText}`}>
                            <div className="inline-flex items-center gap-1.5" title={`CPL: ${formatCurrency(cpl)}${isMetaAdsInvestmentAutomated ? ' · Sincronizado pela Meta Ads' : ''}`}>
                              <span>{formatCurrency(item.investment || 0)}</span>
                              {isMetaAdsInvestmentAutomated && <Link2 className={`h-3 w-3 ${theme.accentText}`} aria-hidden="true" />}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-5 py-4">
                            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${getRoasColorClass(item.roas)}`}>{item.roas.toFixed(2)}x</span>
                          </td>
                          <td className="whitespace-nowrap px-5 py-4">
                            {!isMetaAdsInvestmentAutomated ? (
                              <button
                                type="button"
                                title="Editar investimento mensal"
                                className={`rounded-lg p-2 transition-colors ${isDark ? 'text-[#A6B2C3] hover:bg-[#27364D]/45 hover:text-[#F8FAFC]' : 'text-sminy-muted hover:bg-slate-100 hover:text-sminy-text-primary'}`}
                                onClick={() => handleUpdateInvestment(item)}
                              >
                                <Edit className="h-4 w-4" aria-hidden="true" />
                              </button>
                            ) : (
                              <span title="Investimento sincronizado pela Meta Ads" className={`inline-flex rounded-lg p-2 ${theme.accentText}`}>
                                <Link2 className="h-4 w-4" aria-hidden="true" />
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <footer className={`border-t px-4 py-3 text-xs ${theme.border} ${theme.mutedText}`}>
                Exibindo {filteredMetrics.length} de {metrics.length} {metrics.length === 1 ? 'anúncio' : 'anúncios'}
              </footer>
            </section>
          </>
        )}
      </main>

      {/* MODAL DE VISUALIZACAO DE IMAGEM */}
      <ImagePreviewModal
        imageUrl={previewImageUrl}
        isOpen={isImageModalOpen}
        onClose={handleCloseImageModal}
        title={selectedPreviewCampaign
          ? `${getAdCampaignDisplayName(selectedPreviewCampaign)} · ID do anúncio: ${selectedPreviewCampaign.sourceId}`
          : 'Visualização do Anúncio'}
      />

      {/* MODAL DE EDICAO DE INVESTIMENTO */}
      {selectedCampaign && (
        <InvestmentEditModal
          isOpen={isInvestmentModalOpen}
          onClose={() => setIsInvestmentModalOpen(false)}
          onSave={handleSaveInvestment}
          currentValue={selectedCampaign.investment || 0}
          campaignId={selectedCampaign.sourceId}
          title={`Editar Investimento - ${getAdCampaignDisplayName(selectedCampaign)}`}
          isDark={isDark}
        />
      )}

      <AdCampaignNameEditDialog
        isOpen={campaignBeingNamed !== null}
        isDark={isDark}
        sourceId={campaignBeingNamed?.sourceId || ''}
        initialAlias={campaignBeingNamed?.manualAlias || null}
        isSaving={isCampaignNameSaving}
        errorMessage={campaignNameError}
        onClose={handleCloseCampaignNameDialog}
        onSave={handleSaveCampaignName}
      />

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
      />
    </div>
  );
};

export default AdCampaignDashboard;
